import React, { FC } from "react";
import { Button, Tooltip } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ConfigCardStep, EditingSettingField } from "shared/types";
import { ConfigStepFields } from "shared/api";
import { DynamicFieldsEnum } from "shared/constants";
import { ConfigCardStepFields } from "../ConfigCardStepFields";
import { ProcessStepSettingInput } from "./ProcessStepSettingInput";

const editableFieldTypes = [
  DynamicFieldsEnum.TEXT,
  DynamicFieldsEnum.INTEGER,
  DynamicFieldsEnum.FLOAT,
  DynamicFieldsEnum.CHOICES,
];

interface ProcessStepSettingsCardProps {
  card: ConfigCardStep;
  editingField: EditingSettingField | null;
  isSaving: boolean;
  onEdit: (field: EditingSettingField) => void;
  onSave: () => void;
  onCancel: () => void;
}

const ProcessStepSettingsCard: FC<ProcessStepSettingsCardProps> = ({
  card,
  editingField,
  isSaving,
  onEdit,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();

  const renderValue = (field: ConfigStepFields, fieldIndex: number) => {
    const isEditingField =
      editingField?.cardTitle === card.title &&
      editingField?.fieldName === field.name;

    if (isEditingField) {
      return (
        <div className="min-h-[24px] flex items-center gap-x-1">
          <div className="flex-1 min-w-0">
            <ProcessStepSettingInput
              card={card}
              field={field}
              fieldIndex={fieldIndex}
            />
          </div>
          <Tooltip title={t("configCardColumns.save")}>
            <Button
              size="small"
              type="text"
              icon={<CheckOutlined />}
              loading={isSaving}
              onClick={onSave}
            />
          </Tooltip>
          <Tooltip title={t("configCardColumns.cancel")}>
            <Button
              size="small"
              type="text"
              icon={<CloseOutlined />}
              disabled={isSaving}
              onClick={onCancel}
            />
          </Tooltip>
        </div>
      );
    }

    const isEditable = editableFieldTypes.includes(field.type);

    return (
      <div className="min-h-[24px] flex items-center justify-between gap-x-2">
        <span>{field?.value ?? t("configCardColumns.noValue")}</span>
        {isEditable && (
          <Tooltip title={t("configCardColumns.edit")}>
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              disabled={editingField !== null}
              onClick={() =>
                onEdit({ cardTitle: card.title, fieldName: field.name })
              }
            />
          </Tooltip>
        )}
      </div>
    );
  };

  return <ConfigCardStepFields card={card} renderValue={renderValue} />;
};

export { ProcessStepSettingsCard };
